import React, { useState, useEffect, useRef, useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import api from "../../utils/Api";
import {
  Building2, Hash, Phone, MapPin, Mail, Upload, CheckCircle,
  AlertCircle, RefreshCw, Camera, X,
} from "lucide-react";

// ── Constantes de estilo ───────────────────────────────────────────────────────
const NAVY   = "#0F172A";
const ACCENT = "#FF7A00";
const MUTED  = "#64748B";
const BORDER = "#E2E8F0";

// ── Componente campo con icono ─────────────────────────────────────────────────
function Field({ label, icon, children }) {
  return (
    <div>
      <label style={{
        display: "flex", alignItems: "center", gap: 6,
        fontSize: 11, fontWeight: 700, color: MUTED,
        letterSpacing: "0.7px", textTransform: "uppercase", marginBottom: 8,
      }}>
        {React.cloneElement(icon, { size: 13, color: ACCENT })}
        {label}
      </label>
      {children}
    </div>
  );
}

export default function Empresa() {
  const { token } = useContext(AuthContext);
  const fileRef = useRef(null);
  const [formData, setFormData] = useState({
    nombre_fiscal: "", cif: "", direccion: "", telefono: "", email: "", logo: null,
  });
  const [previewLogo, setPreviewLogo] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [message, setMessage]   = useState({ type: "", text: "" });
  const [dragOver, setDragOver] = useState(false);

  // Base para construir URLs de storage independiente de APP_URL del backend
  const storageBase = (process.env.REACT_APP_API_URL || "").replace(/\/api\/?$/, "/storage");

  useEffect(() => {
    if (!token) return;
    api.get("/empresa", { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        setFormData({
          nombre_fiscal: data.nombre_fiscal || "",
          cif:           data.cif           || "",
          direccion:     data.direccion     || "",
          telefono:      data.telefono      || "",
          email:         data.email         || "",
          logo: null,
        });
        // Construimos la URL del logo con la base correcta (no confiamos en asset() del backend)
        if (data.logo) setPreviewLogo(`${storageBase}/${data.logo}`);
      })
      .catch(() => setMessage({ type: "danger", text: "No se pudo cargar la información de la empresa." }))
      .finally(() => setLoading(false));
  }, [token]);

  const applyLogoFile = (file) => {
    if (!file) return;
    setFormData((p) => ({ ...p, logo: file }));
    setPreviewLogo(URL.createObjectURL(file));
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "logo") applyLogoFile(files[0]);
    else setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file?.type.startsWith("image/")) applyLogoFile(file);
  };

  const removeLogo = () => {
    setPreviewLogo(null);
    setFormData((p) => ({ ...p, logo: null }));
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: "", text: "" });
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("nombre_fiscal", formData.nombre_fiscal || "");
      fd.append("cif",           formData.cif           || "");
      fd.append("direccion",     formData.direccion     || "");
      fd.append("telefono",      formData.telefono      || "");
      fd.append("email",         formData.email         || "");
      if (formData.logo) fd.append("logo", formData.logo);
      await api.post("/empresa", fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setMessage({ type: "success", text: "Datos actualizados correctamente." });
    } catch (err) {
      const errs = err.errors ?? err.response?.data?.errors;
      const firstValidation = errs ? Object.values(errs)[0]?.[0] : null;
      setMessage({ type: "danger", text: firstValidation || err.message || err.response?.data?.message || "Error al guardar los datos." });
    } finally {
      setSaving(false);
    }
  };

  // ── Spinner de carga ──────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, height: 240, color: MUTED, fontSize: 14 }}>
      <RefreshCw size={18} style={{ animation: "spin 1s linear infinite", color: ACCENT }} />
      Cargando datos de empresa…
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  // ── Cabecera hero con identidad de empresa ────────────────────────────────────
  const initials = formData.nombre_fiscal
    ? formData.nombre_fiscal.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase()
    : "E";

  return (
    <div style={{ maxWidth: 780 }}>

      {/* Hero header */}
      <div style={{
        background: `linear-gradient(135deg, ${NAVY} 0%, #1E2947 100%)`,
        borderRadius: 16,
        padding: "28px 32px",
        marginBottom: 24,
        display: "flex",
        alignItems: "center",
        gap: 20,
        position: "relative",
        overflow: "hidden",
      }}>
        {/* Patrón de fondo sutil */}
        <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.05, pointerEvents: "none" }}>
          <defs>
            <pattern id="emp-pat" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse" patternTransform="rotate(25)">
              <line x1="0" y1="0" x2="0" y2="40" stroke="white" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#emp-pat)" />
        </svg>

        {/* Avatar / logo */}
        <div style={{
          width: 72, height: 72, borderRadius: 16, flexShrink: 0,
          overflow: "hidden", position: "relative",
          border: `3px solid rgba(255,255,255,.15)`,
          background: previewLogo ? "transparent" : ACCENT,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {previewLogo
            ? <img src={previewLogo} alt="Logo" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={() => setPreviewLogo(null)} />
            : <span style={{ fontSize: 26, fontWeight: 800, color: "white" }}>{initials}</span>
          }
        </div>

        <div style={{ position: "relative", zIndex: 1 }}>
          <p style={{ fontSize: 12, color: "rgba(255,255,255,.45)", margin: "0 0 4px", fontWeight: 600, letterSpacing: "0.8px", textTransform: "uppercase" }}>Perfil de empresa</p>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: "white", margin: "0 0 4px", letterSpacing: "-0.5px" }}>
            {formData.nombre_fiscal || "Tu empresa"}
          </h1>
          {formData.cif && (
            <span style={{ fontSize: 12, color: "rgba(255,255,255,.45)", fontWeight: 500 }}>CIF: {formData.cif}</span>
          )}
        </div>
      </div>

      {/* Alerta de resultado */}
      {message.text && (
        <div style={{
          display: "flex", alignItems: "flex-start", gap: 10,
          padding: "12px 16px", marginBottom: 20, borderRadius: 10,
          background: message.type === "success" ? "#F0FDF4" : "#FEF2F2",
          border: `1px solid ${message.type === "success" ? "#BBF7D0" : "#FECACA"}`,
        }}>
          {message.type === "success"
            ? <CheckCircle size={16} style={{ color: "#10B981", flexShrink: 0, marginTop: 1 }} />
            : <AlertCircle  size={16} style={{ color: "#EF4444", flexShrink: 0, marginTop: 1 }} />
          }
          <span style={{ fontSize: 13, fontWeight: 500, color: message.type === "success" ? "#15803D" : "#7F1D1D" }}>
            {message.text}
          </span>
        </div>
      )}

      <form onSubmit={handleSubmit}>

        {/* ── Sección: Datos legales ── */}
        <div className="tn-card" style={{ padding: "24px 28px", marginBottom: 16 }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: MUTED, letterSpacing: "0.8px", textTransform: "uppercase", margin: "0 0 20px", display: "flex", alignItems: "center", gap: 6 }}>
            <Building2 size={13} color={ACCENT} /> Datos legales
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 18 }}>
            <div style={{ gridColumn: "1 / -1" }}>
              <Field label="Nombre fiscal" icon={<Building2 />}>
                <input className="tn-input" type="text" name="nombre_fiscal"
                  value={formData.nombre_fiscal} onChange={handleChange} required
                  placeholder="Turnora S.L." />
              </Field>
            </div>
            <Field label="CIF / NIF" icon={<Hash />}>
              <input className="tn-input" type="text" name="cif"
                value={formData.cif} onChange={handleChange} required
                placeholder="B12345678" />
            </Field>
            <Field label="Teléfono" icon={<Phone />}>
              <input className="tn-input" type="tel" name="telefono"
                value={formData.telefono} onChange={handleChange}
                placeholder="000 000 000" />
            </Field>
            <div style={{ gridColumn: "1 / -1" }}>
              <Field label="Dirección" icon={<MapPin />}>
                <input className="tn-input" type="text" name="direccion"
                  value={formData.direccion} onChange={handleChange} required
                  placeholder="Calle Ejemplo 1, 28001 Madrid" />
              </Field>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <Field label="Correo electrónico" icon={<Mail />}>
                <input className="tn-input" type="email" name="email"
                  value={formData.email} onChange={handleChange} required
                  placeholder="hola@tuempresa.es" />
              </Field>
            </div>
          </div>
        </div>

        {/* ── Sección: Logotipo ── */}
        <div className="tn-card" style={{ padding: "24px 28px", marginBottom: 24 }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: MUTED, letterSpacing: "0.8px", textTransform: "uppercase", margin: "0 0 20px", display: "flex", alignItems: "center", gap: 6 }}>
            <Camera size={13} color={ACCENT} /> Logotipo
          </p>

          <div style={{ display: "flex", gap: 24, alignItems: "flex-start", flexWrap: "wrap" }}>
            {/* Preview */}
            <div style={{ position: "relative", flexShrink: 0 }}>
              <div style={{
                width: 96, height: 96, borderRadius: 14,
                border: `2px solid ${BORDER}`,
                background: previewLogo ? "transparent" : "#F8FAFC",
                display: "flex", alignItems: "center", justifyContent: "center",
                overflow: "hidden",
              }}>
                {previewLogo
                  ? <img src={previewLogo} alt="Logo" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={() => setPreviewLogo(null)} />
                  : <Building2 size={32} color={BORDER} />
                }
              </div>
              {previewLogo && (
                <button type="button" onClick={removeLogo} style={{
                  position: "absolute", top: -8, right: -8,
                  width: 22, height: 22, borderRadius: "50%",
                  background: "#EF4444", border: "2px solid white",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer", padding: 0,
                }}>
                  <X size={11} color="white" />
                </button>
              )}
            </div>

            {/* Zona de drop */}
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileRef.current?.click()}
              style={{
                flex: 1, minWidth: 200, minHeight: 96,
                border: `2px dashed ${dragOver ? ACCENT : BORDER}`,
                borderRadius: 12,
                background: dragOver ? "#FFF7ED" : "#FAFAFA",
                display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                gap: 8, cursor: "pointer",
                transition: "border-color 0.15s, background 0.15s",
                padding: "20px 16px",
              }}
            >
              <Upload size={20} color={dragOver ? ACCENT : MUTED} />
              <span style={{ fontSize: 13, color: dragOver ? ACCENT : MUTED, fontWeight: 500, textAlign: "center" }}>
                Arrastra una imagen aquí o <span style={{ color: ACCENT, fontWeight: 700 }}>haz clic</span> para seleccionarla
              </span>
              <span style={{ fontSize: 11, color: "#94A3B8" }}>PNG, JPG, SVG — máx. 2 MB</span>
              <input
                ref={fileRef}
                type="file"
                name="logo"
                accept="image/*"
                onChange={handleChange}
                style={{ display: "none" }}
              />
            </div>
          </div>
        </div>

        {/* Botón guardar */}
        <button
          type="submit"
          disabled={saving}
          style={{
            width: "100%", height: 50,
            background: saving ? "#CBD5E1" : NAVY,
            color: "white", border: "none", borderRadius: 12,
            fontSize: 15, fontWeight: 700, letterSpacing: "-0.2px",
            cursor: saving ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            fontFamily: "inherit", transition: "background 0.15s",
          }}
          onMouseEnter={(e) => !saving && (e.currentTarget.style.background = "#1E2947")}
          onMouseLeave={(e) => !saving && (e.currentTarget.style.background = NAVY)}
        >
          {saving ? (
            <>
              <span style={{ width: 16, height: 16, border: "2px solid rgba(255,255,255,.3)", borderTop: "2px solid white", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
              Guardando…
            </>
          ) : "Guardar cambios"}
        </button>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </form>
    </div>
  );
}
